/* eslint-disable */
/*
 * Catalog of public RMS API endpoints. This drives the /api-docs page.
 * Adding an endpoint is O(1) — append an object to the right module's
 * `endpoints` array. Keep the shape consistent so ApiDocs.vue can render
 * it without special-casing.
 *
 * Substitute `{county}` in every URL with the target county slug at request
 * time (e.g. `meru`, `kajiado`, `mandera`, `turkana`, `homabay`).
 */

export const apiSpec = {
  baseUrl: 'https://api.craftcollect.africa/{county}',
  version: 'v1',
  contact: {
    email: 'revenue@county.go.ke',
    supportHours: 'Mon–Fri, 08:00–17:00 EAT'
  },

  overview: {
    intro: `All RMS county revenue endpoints are HTTPS-only, return JSON, and are
      currently on version 1. Breaking changes will land under /v2/… and both
      versions will run in parallel for at least 6 months before v1 is retired.`,
    contentType: `Requests use multipart/form-data (or application/json for
      webhook callbacks). Responses are always application/json with UTF-8
      encoding.`,
    envelope: `Every response is wrapped in a common envelope:

  {
    "status": 200,
    "success": true|false,
    "message": "human-readable message",
    "data": { ... }   // endpoint-specific payload
  }`,
    sla: `Service level commitments:

  Uptime target        99.5% monthly (measured externally by UptimeRobot
                       + Pingdom against the /health endpoint per county).
  Latency target       95th percentile ≤ 2s for read endpoints, ≤ 5s for
                       write endpoints, measured over rolling 24h.
  Planned maintenance  Announced 72h in advance via revenue@county.go.ke;
                       executed Sundays 00:00–02:00 EAT.
  Incident response    Sev-1 (payment path down) — 30 min; Sev-2 (reports
                       degraded) — 4h; Sev-3 (cosmetic) — next business day.

Real-time status board: https://status.craftcollect.africa/{county}`,
    versioning: `API version is embedded in the path: /v1/. Non-breaking
      additions (new fields, new endpoints) land in v1 continuously. Breaking
      changes (renamed fields, changed semantics, removed endpoints) require
      a new /v2/ path; the old version stays live for at least 6 months. All
      versions honour the same envelope + auth scheme.`,
  },

  authentication: {
    summary: `Token-based bearer auth using the OpenID Connect / OAuth 2.0
      Resource Owner Password Credentials pattern. Staff clients POST username
      + password to /authentication/index.php?function=loginPortal and receive
      a session token valid for 1 hour (renewable). Citizen clients use a
      passwordless SMS-OTP flow (see /authentication citizenRequestOtp +
      citizenVerifyOtp). Machine-to-machine callers use pre-provisioned
      long-lived tokens issued by the county admin.`,
    header: `Send the token as a bearer credential:

  Authorization: Bearer <token>

Alternatively (for backward compatibility) staff endpoints accept the token
as a form field named `,
    tokenTtl: 'Staff session: 60 minutes (auto-refresh on activity). Citizen session: 30 days. M2M: county-controlled.',
    scopes: `Rights are enforced per-endpoint against the caller's role
      selectedRoles (see the "Rights" reference at the county admin panel).
      Missing right → 200 OK with success=false and a message ending in
      "You do not have the right".`
  },

  throttling: {
    policy: `Per-caller rate limits are enforced at the API gateway:

  Anonymous:           60 requests / minute per IP
  Staff (bearer):     600 requests / minute per session token
  Citizen (bearer):   120 requests / minute per session token
  M2M / integrators:  Configurable per client — default 6000/min

Bulk read endpoints (statements, reports) have their own dedicated limits
documented per-endpoint.`,
    response: `When exceeded, the API returns HTTP 429 Too Many Requests with:

  {
    "status": 429,
    "success": false,
    "message": "Rate limit exceeded. Try again in N seconds.",
    "data": { "retryAfter": N }
  }

The Retry-After header is also set.`
  },

  errors: {
    envelope: `All errors — validation, business-rule, or system — return the
      standard envelope with success=false. HTTP status codes:

  200  request accepted (check success flag for business-rule outcome)
  400  malformed request
  401  missing / invalid / expired token
  403  authenticated but lacks the required right
  404  endpoint or resource not found
  409  conflict (e.g. duplicate submission)
  422  validation failed
  429  rate limit exceeded
  500  server-side error — safe to retry with backoff`
  },

  webhooks: {
    intro: `The RMS pushes events to subscriber URLs registered per stream. All
      outbound webhooks retry on non-2xx responses with exponential backoff
      (10s, 60s, 300s, 1800s) up to 4 attempts. Payloads are signed with an
      HMAC-SHA256 signature in the X-RMS-Signature header.`,
    events: [
      {
        event: 'payment.received',
        description: 'A payment landed and reconciled against a bill.',
        payload: { billNo: 'BLL...', receiptNo: 'RC...', amount: 1000, transactionCode: '...', paymentMode: 'MPESA', dateReceived: '2026-08-27T09:35:00Z' }
      },
      {
        event: 'invoice.approved',
        description: 'A gated invoice (streams.printable=false) was approved.',
        payload: { billNo: 'BLL...', approvedBy: 'idNo', approvedAt: '2026-08-27T09:35:00Z' }
      },
      {
        event: 'permit.issued',
        description: 'An SBP permit was issued for a business.',
        payload: { businessID: '123456789', billNo: 'BLL...', startDate: '2026-01-01', endDate: '2026-12-31' }
      }
    ],
    verification: `Verify the X-RMS-Signature header against your shared secret:

  hmac_sha256(rawBody, sharedSecret) == req.headers['x-rms-signature']

Reject any payload where the signatures do not match. Idempotency key is in
the X-RMS-Event-Id header — safe to replay the same event id.`
  },

  modules: [
    {
      id: 'authentication',
      name: 'Authentication',
      summary: 'Staff login, citizen OTP sign-in, session management.',
      endpoints: [
        {
          id: 'loginPortal',
          name: 'Staff Login',
          method: 'POST',
          path: '/authentication/index.php',
          function: 'loginPortal',
          summary: 'Authenticate a staff account and mint a session token.',
          auth: 'None — this endpoint issues the token.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'loginPortal' },
            { field: 'email', type: 'string', required: true, notes: 'ID number or registered email' },
            { field: 'password', type: 'string', required: true, notes: 'Plain text or base64-encoded — auto-detected' },
            { field: 'latitude', type: 'number', required: false, notes: 'Enables geofence check' },
            { field: 'longitude', type: 'number', required: false },
            { field: 'deviceDetails', type: 'string', required: false, notes: 'Free-form; logged to audit trail' },
            { field: 'versionCode', type: 'string', required: false, notes: 'Mobile APK versionCode (integer) or web semver (e.g. 1.0.0)' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/authentication/index.php" \\
  -F "function=loginPortal" \\
  -F "email=22082287" \\
  -F "password=YOUR_PASSWORD" \\
  -F "deviceDetails=Web browser"`,
          responseSuccess: {
            status: 200,
            success: true,
            message: 'Login successful',
            data: {
              tokenSession: { token: 'a1b2c3…', expiry: '2026-08-27 10:35:00' },
              otpLength: 4,
              user: { idNo: '22082287', names: 'Alex Wafula Wanjala', category: 'CASHIER', subCountyName: 'MERU TOWN' },
              category: { selectedRoles: [{ role: 'PRINT INVOICE' }, { role: 'RECEIPT CASH PAYMENT' }] }
            }
          },
          responseErrors: [
            { code: '"Invalid username or password."', when: 'Unknown identifier or wrong password.' },
            { code: '"Account Not Approved"', when: 'Account exists but approval flag is false.' },
            { code: '"Your password has expired and requires reset."', when: 'passwordExpiry ≤ now.' },
            { code: '"Your account has been deactivated"', when: 'status != "Active".' }
          ]
        },
        {
          id: 'citizenRequestOtp',
          name: 'Citizen — Request OTP',
          method: 'POST',
          path: '/authentication/index.php',
          function: 'citizenRequestOtp',
          summary: 'Send a 6-digit SMS OTP to a customer phone number. Idempotent per-hour up to 5 requests.',
          auth: 'None.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'citizenRequestOtp' },
            { field: 'phoneNumber', type: 'string', required: true, notes: 'Any format — normalized to 254XXXXXXXXX' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/authentication/index.php" \\
  -F "function=citizenRequestOtp" \\
  -F "phoneNumber=0704722837"`,
          responseSuccess: {
            status: 200,
            success: true,
            message: 'OTP sent',
            data: { phone: '254704722837', expiresAt: '2026-08-27 09:40:00' }
          },
          responseErrors: [
            { code: '"Enter a valid phone number"', when: 'Phone fails length/digit validation.' },
            { code: '"Too many OTP requests. Please try again in an hour."', when: 'More than 5 requests for the same phone in the last hour.' }
          ]
        },
        {
          id: 'citizenVerifyOtp',
          name: 'Citizen — Verify OTP',
          method: 'POST',
          path: '/authentication/index.php',
          function: 'citizenVerifyOtp',
          summary: 'Exchange phone + OTP for a 30-day citizen session token.',
          auth: 'None — this endpoint issues the token.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'citizenVerifyOtp' },
            { field: 'phoneNumber', type: 'string', required: true },
            { field: 'otp', type: 'string', required: true, notes: '6-digit code from SMS' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/authentication/index.php" \\
  -F "function=citizenVerifyOtp" \\
  -F "phoneNumber=0704722837" \\
  -F "otp=123456"`,
          responseSuccess: {
            status: 200,
            success: true,
            message: 'Signed in',
            data: { token: 'a1b2c3…', expiresAt: '2026-09-26 09:35:00', phone: '254704722837', displayName: 'John Doe' }
          },
          responseErrors: [
            { code: '"Invalid or expired code"', when: 'OTP wrong or older than 5 minutes.' }
          ]
        }
      ]
    },
    {
      id: 'biller',
      name: 'Biller',
      summary: 'Bill lookup, payment initiation, receipt retrieval.',
      endpoints: [
        {
          id: 'makePayment',
          name: 'Make Payment',
          method: 'POST',
          path: '/biller/index.php',
          function: 'makePayment',
          summary: 'Trigger a Safaricom M-Pesa STK push against a bill.',
          auth: 'Bearer token (staff or citizen).',
          request: [
            { field: 'function', type: 'string', required: true, example: 'makePayment' },
            { field: 'paymentData', type: 'json (string)', required: true, notes: 'JSON body with accNo, payBillNo, amount, phoneNumber, paymentMode, sessionNames, sessionIdNo' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -F "function=makePayment" \\
  -F 'paymentData={"accNo":"BLL0507024528973","payBillNo":"440112","amount":"20","phoneNumber":"0704722837","paymentMode":"MPESA","sessionNames":"Cashier","sessionIdNo":"22082287"}'`,
          responseSuccess: {
            status: 200,
            success: true,
            message: 'Payment request sent on your phone.',
            data: { accNo: 'BLL0507024528973', payBillNo: '440112', amount: '20', success: true }
          },
          responseErrors: [
            { code: '"MPESA service is temporarily unavailable. Please try again in a moment."', when: 'Paysol relay failed.' },
            { code: '"The bill balance is KES X; therefore, the amount cannot be less than this."', when: 'Amount below outstanding balance.' }
          ]
        },
        {
          id: 'generateBill',
          name: 'Generate Bill',
          method: 'POST',
          path: '/biller/index.php',
          function: 'generateBill',
          summary: 'Create a new invoice from a list of line items. Returns a random 13-digit BLL bill number.',
          auth: 'Bearer token (staff).',
          request: [
            { field: 'function', type: 'string', required: true, example: 'generateBill' },
            { field: 'billItem', type: 'json array (string)', required: true, notes: 'Array of line items — each with feeId, amount, customer, subCountyID, wardID, phoneNumber, names, idNo, customerName, customerPhoneNumber' },
            { field: 'penalized', type: 'boolean', required: false, notes: 'Apply late-payment penalty' },
            { field: 'payNow', type: 'boolean', required: false, notes: 'If true, immediately fires STK. Ignored when the stream is approval-gated.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -F "function=generateBill" \\
  -F 'billItem=[{"feeId":"1944","amount":40000,"customer":"","subCountyID":"120572","wardID":"120572583","idNo":"22082287","phoneNumber":"254704722837","names":"Cashier","customerName":"Alex Wanjala","customerPhoneNumber":"0704722837"}]'`,
          responseSuccess: {
            status: 200,
            success: true,
            message: 'Invoice generated',
            data: {
              billGenerated: { billNo: 'BLL7720209304818', payBillNo: '340134', amount: '40000', printable: 'true', success: true },
              bill: { billDetails: '…', billInfo: '…' }
            }
          },
          responseErrors: [
            { code: '"SQL Error: Duplicate entry ..."', when: 'Never fires now — server-side line aggregation prevents duplicate (billNo, feeID, customer) tuples.' }
          ]
        },
        {
          id: 'citizenSummary',
          name: 'Citizen — Dashboard Summary',
          method: 'POST',
          path: '/biller/index.php',
          function: 'citizenSummary',
          summary: 'Return arrears + recent bills + recent receipts scoped to the citizen session.',
          auth: 'Citizen bearer token.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'citizenSummary' },
            { field: 'token', type: 'string', required: true, notes: 'From citizenVerifyOtp' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -F "function=citizenSummary" \\
  -F "token=YOUR_TOKEN"`,
          responseSuccess: {
            status: 200,
            success: true,
            message: 'OK',
            data: { phone: '254704722837', unpaidCount: 2, unpaidTotal: 3400, recentBills: '[...]', recentReceipts: '[...]' }
          },
          responseErrors: [
            { code: '"Session expired"', when: 'Token invalid or older than 30 days.' }
          ]
        },
        {
          id: 'getStreamApproval',
          name: 'Get Stream Approval Requirement',
          method: 'POST',
          path: '/biller/index.php',
          function: 'getStreamApproval',
          summary: 'Check whether a revenue stream requires invoice approval before payment (per countyBiller.streams.printable).',
          auth: 'None.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'getStreamApproval' },
            { field: 'incomeTypePrefix', type: 'string', required: true, notes: 'Stream prefix: SBP, PKN, MKT, LANDRATE, PLANS, etc.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -F "function=getStreamApproval" \\
  -F "incomeTypePrefix=SBP"`,
          responseSuccess: {
            status: 200,
            success: true,
            message: 'OK',
            data: { incomeTypePrefix: 'SBP', stream: 'SINGLE BUSINESS PERMIT', printable: 'false', requiresApproval: true, approverCount: 3 }
          }
        }
      ]
    },
    {
      id: 'paysol',
      name: 'Paysol',
      summary: 'Direct M-Pesa STK push and payment status polling.',
      endpoints: [
        {
          id: 'customerPayBillOnline',
          name: 'STK Push',
          method: 'POST',
          path: '/paysol/index.php',
          function: 'customerPayBillOnline',
          sensitive: true,
          sensitiveNote: 'Live M-Pesa integration. Access is restricted to authorised staff and machine-to-machine partners only. Do not share credentials, paybills, or sample responses outside your integration engagement.',
          summary: 'Send an M-Pesa STK push directly (bypasses biller relay).',
          auth: 'Bearer token (staff or M2M).',
          request: [
            { field: 'function', type: 'string', required: true, example: 'customerPayBillOnline' },
            { field: 'payBillNumber', type: 'string', required: true, notes: 'The paybill configured for the target county — do not hardcode.' },
            { field: 'amount', type: 'number', required: true },
            { field: 'phoneNumber', type: 'string', required: true },
            { field: 'accountReference', type: 'string', required: true, notes: 'BLL bill number' },
            { field: 'transactionDesc', type: 'string', required: false }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/paysol/index.php" \\
  -F "function=customerPayBillOnline" \\
  -F "payBillNumber=<county-paybill>" \\
  -F "amount=20" \\
  -F "phoneNumber=<msisdn>" \\
  -F "accountReference=<BLL-bill-number>"`,
          responseSuccess: {
            status: 200,
            success: true,
            message: 'success',
            data: { PushRequest: { payBillNumber: '<redacted>', phoneNumber: '<msisdn>', amount: 20, accountReference: '<BLL-bill-number>' } }
          }
        }
      ]
    },
    {
      id: 'configuration',
      name: 'Configuration',
      summary: 'Read-only lookups for streams, sub-counties, wards, zones, fees, users, and module toggles. Write endpoints exist on the admin panel but are not part of the public API surface.',
      endpoints: [
        {
          id: 'listStreams',
          name: 'List Revenue Streams',
          method: 'POST',
          path: '/biller/index.php',
          function: 'listStreams',
          summary: 'Returns every revenue stream configured for the county — incomeTypePrefix, department, printable flag, and the paybill/shortcode it collects on.',
          auth: 'Bearer token (staff or M2M).',
          request: [
            { field: 'function', type: 'string', required: true, example: 'listStreams' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=listStreams"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { streams: [
              { incomeTypePrefix: 'SBP', incomeTypeDescription: 'Single Business Permit', department: 'Trade', shortCode: '<county-paybill>', printable: 'true', status: '1' },
              { incomeTypePrefix: 'ADVT', incomeTypeDescription: 'Advertising', department: 'Trade', shortCode: '<county-paybill>', printable: 'false', status: '1' }
            ] }
          }
        },
        {
          id: 'listSubCountiesWards',
          name: 'List Sub-Counties, Wards & Zones',
          method: 'POST',
          path: '/authentication/index.php',
          function: 'listGeoHierarchy',
          summary: 'Returns the full geographic hierarchy — sub-counties, wards within each sub-county, and zones within each ward. Used by clients that need to bind bills to a location.',
          auth: 'Bearer token (staff or M2M).',
          request: [
            { field: 'function', type: 'string', required: true, example: 'listGeoHierarchy' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/authentication/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=listGeoHierarchy"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { subCounties: [
              { subCountyID: '120591', subCountyName: 'MERU TOWN', wards: [
                { wardID: '120591291', wardName: 'MERU MUNICIPALITY', zones: [{ zoneID: '...', zoneName: 'CBD' }] }
              ] }
            ] }
          }
        },
        {
          id: 'listFees',
          name: 'List Fees & Charges',
          method: 'POST',
          path: '/biller/index.php',
          function: 'listFees',
          summary: 'Returns the fee master — every feeID, its description, income type, default amount, and the fiscal year it applies to.',
          auth: 'Bearer token (staff or M2M).',
          request: [
            { field: 'function', type: 'string', required: true, example: 'listFees' },
            { field: 'incomeTypePrefix', type: 'string', required: false, notes: 'Filter to one stream (e.g. SBP). Omit for all streams.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=listFees" \\
  -F "incomeTypePrefix=SBP"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { fees: [
              { feeID: '1944', feeDescription: 'General Retail Trader — Medium', incomeTypePrefix: 'SBP', defaultAmount: '40000', fiscalYear: '2026/2027' }
            ] }
          }
        },
        {
          id: 'listUsers',
          name: 'List Staff Users',
          method: 'POST',
          path: '/authentication/index.php',
          function: 'listUsers',
          summary: 'Returns staff users with their role assignments, subcounty scope, and last-login timestamp. Passwords, tokens, and OTP fields are never emitted.',
          auth: 'Bearer token (admin scope required).',
          request: [
            { field: 'function', type: 'string', required: true, example: 'listUsers' },
            { field: 'active', type: 'boolean', required: false, notes: 'Filter to active only.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/authentication/index.php" \\
  -H "Authorization: Bearer <admin-token>" \\
  -F "function=listUsers" \\
  -F "active=true"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { users: [
              { idNo: '<staff-id>', names: '<staff-name>', roleID: 'CASHIER', subCounties: ['MERU TOWN'], lastLogin: '2026-08-27T09:30:00Z' }
            ] }
          }
        },
        {
          id: 'listFeesForScoa',
          name: 'List Fees with SCOA Mapping',
          method: 'POST',
          path: '/biller/index.php',
          function: 'listFeesForScoa',
          summary: 'Paged list of fees with their SCOA (Standard Chart of Accounts) codes — Vote, Programme, Sub-Programme, Item, Sub-Item, Fund. Backs the /scoa-mapping admin page.',
          auth: 'Bearer token with FINANCE ADMIN right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'listFeesForScoa' },
            { field: 'q', type: 'string', required: false, notes: 'Search feeId / feeDescription / scoaCode.' },
            { field: 'missingOnly', type: 'boolean', required: false, notes: 'Filter to fees without a scoaCode set.' },
            { field: 'page', type: 'number', required: false, notes: 'Default 1.' },
            { field: 'pageSize', type: 'number', required: false, notes: 'Default 100, max 500.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=listFeesForScoa" -F "missingOnly=true"`,
          responseSuccess: {
            status: 200, success: true, message: 'OK',
            data: { page: 1, pageSize: 100, total: 1420, totalPages: 15, rows: [
              { feeId: '1944', feeDescription: '…', scoaCode: 'R11-01-00-1420101', scoaVote: 'R11', scoaItem: '1420101', scoaFund: 'CRF' }
            ] }
          }
        },
        {
          id: 'updateFeeScoa',
          name: 'Update Fee SCOA Mapping',
          method: 'POST',
          path: '/biller/index.php',
          function: 'updateFeeScoa',
          summary: 'Write the SCOA mapping for one fee. Empty strings clear that segment. scoaUpdatedBy + scoaUpdatedAt are stamped from the caller session.',
          auth: 'Bearer token with FINANCE ADMIN right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'updateFeeScoa' },
            { field: 'feeId', type: 'string', required: true },
            { field: 'scoaCode', type: 'string', required: false, notes: 'Full compact SCOA code (e.g. R11-01-00-1420101).' },
            { field: 'scoaVote', type: 'string', required: false },
            { field: 'scoaProgramme', type: 'string', required: false },
            { field: 'scoaSubProgramme', type: 'string', required: false },
            { field: 'scoaItem', type: 'string', required: false, notes: '4-digit economic classification code.' },
            { field: 'scoaSubItem', type: 'string', required: false, notes: '7-digit sub-item if the county uses one.' },
            { field: 'scoaFund', type: 'string', required: false, notes: 'CRF, County Own Source, Donor, etc.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <admin-token>" \\
  -F "function=updateFeeScoa" -F "feeId=1944" \\
  -F "scoaCode=R11-01-00-1420101" -F "scoaVote=R11" -F "scoaItem=1420101" -F "scoaFund=CRF"`,
          responseSuccess: {
            status: 200, success: true, message: 'Saved',
            data: { feeId: '1944', scoaUpdatedBy: '<staff-idNo>' }
          }
        },
        {
          id: 'listFeesMissingScoa',
          name: 'Audit: Fees Missing SCOA',
          method: 'POST',
          path: '/biller/index.php',
          function: 'listFeesMissingScoa',
          summary: 'Returns the count and up to 100 sample fees that have no scoaCode set. Used by the SCOA Mapping and IFMIS Export pages to warn admins before running an export.',
          auth: 'Bearer token.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'listFeesMissingScoa' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=listFeesMissingScoa"`,
          responseSuccess: {
            status: 200, success: true, message: 'OK',
            data: { missingCount: 74, sample: [{ feeId: '83878', feeDescription: 'Vehicle Branding', incomeTypeId: '161' }] }
          }
        },
        {
          id: 'listModuleToggles',
          name: 'List Module Toggles',
          method: 'POST',
          path: '/authentication/index.php',
          function: 'listModuleToggles',
          summary: 'Returns which self-service and staff modules are enabled for this county (SBP, park, land-rates, plot-rent, etc.) so clients can adapt their UI.',
          auth: 'Bearer token.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'listModuleToggles' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/authentication/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=listModuleToggles"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { selfServiceModules: { sbp: true, park: false, landRates: true }, staffModules: { audit: true, reports: true } }
          }
        }
      ]
    },
    {
      id: 'periodClose',
      name: 'Period Close',
      summary: 'Fiscal-year and month-end operations — list periods, freeze/unfreeze windows, monthly rollups, and closing-entry generation.',
      endpoints: [
        {
          id: 'listFiscalYears',
          name: 'List Fiscal Years',
          method: 'POST',
          path: '/biller/index.php',
          function: 'listFiscalYears',
          summary: 'Returns every fiscal year on record, current status (OPEN / CLOSED / LOCKED), and the closing date if closed.',
          auth: 'Bearer token.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'listFiscalYears' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=listFiscalYears"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { years: [
              { fiscalYear: '2025/2026', startDate: '2025-07-01', endDate: '2026-06-30', status: 'CLOSED', closedAt: '2026-07-15T18:00:00Z' },
              { fiscalYear: '2026/2027', startDate: '2026-07-01', endDate: '2027-06-30', status: 'OPEN', closedAt: null }
            ] }
          }
        },
        {
          id: 'periodStatus',
          name: 'Period Status',
          method: 'POST',
          path: '/biller/index.php',
          function: 'periodStatus',
          summary: 'Returns whether the given month is OPEN, FROZEN (read-only), or CLOSED. Frozen periods reject new receipts and adjustments.',
          auth: 'Bearer token.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'periodStatus' },
            { field: 'year', type: 'string', required: true, example: '2026' },
            { field: 'month', type: 'string', required: true, example: '08' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=periodStatus" \\
  -F "year=2026" -F "month=08"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { year: '2026', month: '08', status: 'OPEN', closedAt: null }
          }
        },
        {
          id: 'closePeriod',
          name: 'Close Period (Month-End)',
          method: 'POST',
          path: '/biller/index.php',
          function: 'closePeriod',
          sensitive: true,
          sensitiveNote: 'Destructive — freezes all writes for the named period. Reversal requires admin re-open with an audit-trail entry. Only call after month-end reconciliation is confirmed.',
          summary: 'Freezes a fiscal period — no new receipts, transactions, or adjustments accepted for that (year, month). Idempotent: closing an already-closed period is a no-op.',
          auth: 'Bearer token with FINANCE ADMIN right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'closePeriod' },
            { field: 'year', type: 'string', required: true, example: '2026' },
            { field: 'month', type: 'string', required: true, example: '07' },
            { field: 'reason', type: 'string', required: true, notes: 'Free-text note stored in the close-audit log.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <admin-token>" \\
  -F "function=closePeriod" \\
  -F "year=2026" -F "month=07" \\
  -F "reason=Month-end 2026-07 reconciled and signed off"`,
          responseSuccess: {
            status: 200, success: true, message: 'Period 2026-07 closed',
            data: { year: '2026', month: '07', closedBy: '<staff-idNo>', closedAt: '2026-08-05T14:00:00Z' }
          }
        },
        {
          id: 'monthlyRollup',
          name: 'Monthly Rollup',
          method: 'POST',
          path: '/biller/index.php',
          function: 'monthlyRollup',
          summary: 'Returns aggregated receipt totals per stream/sub-county/ward for the given month. Used for month-end reports and closing-entry generation.',
          auth: 'Bearer token.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'monthlyRollup' },
            { field: 'year', type: 'string', required: true, example: '2026' },
            { field: 'month', type: 'string', required: true, example: '07' },
            { field: 'groupBy', type: 'string', required: false, notes: 'One of: stream, subCounty, ward, department. Default: stream.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=monthlyRollup" \\
  -F "year=2026" -F "month=07" -F "groupBy=stream"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { year: '2026', month: '07', groupBy: 'stream', rows: [
              { incomeTypePrefix: 'SBP', receipts: 1250, totalAmount: 25770000 },
              { incomeTypePrefix: 'LANDRATE', receipts: 340, totalAmount: 8940000 }
            ] }
          }
        }
      ]
    },
    {
      id: 'reports',
      name: 'Reporting',
      summary: 'Read-only revenue and performance reports — the same aggregates that drive /ranking, /reports-streams, /reports-sub-county, /department-performance in the staff portal. All reports honour the RECONCILED-REVENUE rule (SUM(receiptAmount), status=PAID, code=1, exclude UNINVOICED PAYMENTS).',
      endpoints: [
        {
          id: 'revenueByStream',
          name: 'Revenue by Stream',
          method: 'POST',
          path: '/biller/index.php',
          function: 'revenueByStream',
          summary: 'Total receipted amount grouped by revenue stream (incomeTypePrefix) for a date range. Matches /reports-streams in the staff portal exactly.',
          auth: 'Bearer token with REPORTS right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'revenueByStream' },
            { field: 'from', type: 'string', required: true, notes: 'YYYY-MM-DD (inclusive).' },
            { field: 'to', type: 'string', required: true, notes: 'YYYY-MM-DD (inclusive).' },
            { field: 'incomeTypePrefix', type: 'string', required: false, notes: 'Filter to a single stream. Omit for all.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=revenueByStream" \\
  -F "from=2026-07-01" -F "to=2026-07-31"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { from: '2026-07-01', to: '2026-07-31', rows: [
              { incomeTypePrefix: 'SBP', receipts: 1250, totalAmount: 25770000 },
              { incomeTypePrefix: 'PARKING', receipts: 8940, totalAmount: 4470000 }
            ] }
          }
        },
        {
          id: 'revenueBySubCounty',
          name: 'Revenue by Sub-County',
          method: 'POST',
          path: '/biller/index.php',
          function: 'revenueBySubCounty',
          summary: 'Total receipted amount grouped by sub-county for a date range. Matches /reports-sub-county in the staff portal.',
          auth: 'Bearer token with REPORTS right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'revenueBySubCounty' },
            { field: 'from', type: 'string', required: true, notes: 'YYYY-MM-DD.' },
            { field: 'to', type: 'string', required: true, notes: 'YYYY-MM-DD.' },
            { field: 'incomeTypePrefix', type: 'string', required: false, notes: 'Filter to one stream.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=revenueBySubCounty" \\
  -F "from=2026-07-01" -F "to=2026-07-31"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { rows: [
              { subCountyName: 'MERU TOWN', receipts: 5400, totalAmount: 12300000 },
              { subCountyName: 'NORTH IMENTI', receipts: 2100, totalAmount: 4780000 }
            ] }
          }
        },
        {
          id: 'revenueByWard',
          name: 'Revenue by Ward',
          method: 'POST',
          path: '/biller/index.php',
          function: 'revenueByWard',
          summary: 'Total receipted amount grouped by ward for a date range. Matches /reports-streams-wards.',
          auth: 'Bearer token with REPORTS right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'revenueByWard' },
            { field: 'from', type: 'string', required: true },
            { field: 'to', type: 'string', required: true },
            { field: 'subCountyID', type: 'string', required: false, notes: 'Restrict to wards inside this sub-county.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=revenueByWard" \\
  -F "from=2026-07-01" -F "to=2026-07-31"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { rows: [{ wardName: 'MERU MUNICIPALITY', wardID: '120591291', receipts: 2200, totalAmount: 5100000 }] }
          }
        },
        {
          id: 'revenueByZone',
          name: 'Revenue by Zone',
          method: 'POST',
          path: '/biller/index.php',
          function: 'revenueByZone',
          summary: 'Total receipted amount grouped by zone. Matches /reports-streams-zones.',
          auth: 'Bearer token with REPORTS right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'revenueByZone' },
            { field: 'from', type: 'string', required: true },
            { field: 'to', type: 'string', required: true },
            { field: 'wardID', type: 'string', required: false }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=revenueByZone" \\
  -F "from=2026-07-01" -F "to=2026-07-31"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { rows: [{ zoneName: 'CBD', receipts: 400, totalAmount: 890000 }] }
          }
        },
        {
          id: 'ranking',
          name: 'Collector Ranking',
          method: 'POST',
          path: '/biller/index.php',
          function: 'ranking',
          summary: 'Top-N staff by receipts collected in the given range. Matches /ranking in the staff portal.',
          auth: 'Bearer token with REPORTS right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'ranking' },
            { field: 'from', type: 'string', required: true },
            { field: 'to', type: 'string', required: true },
            { field: 'limit', type: 'number', required: false, notes: 'Default 20, max 100.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=ranking" \\
  -F "from=2026-07-01" -F "to=2026-07-31" -F "limit=10"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { rows: [{ receiptedBy: '<staff-idNo>', names: '<staff-name>', receipts: 340, totalAmount: 890000 }] }
          }
        },
        {
          id: 'departmentPerformance',
          name: 'Department Performance',
          method: 'POST',
          path: '/biller/index.php',
          function: 'departmentPerformance',
          summary: 'Actual vs target receipts per department for the fiscal year. Matches /department-performance.',
          auth: 'Bearer token with REPORTS right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'departmentPerformance' },
            { field: 'fiscalYear', type: 'string', required: true, example: '2026/2027' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=departmentPerformance" \\
  -F "fiscalYear=2026/2027"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { rows: [{ departmentID: '3', department: 'Trade & Industry', target: 30000000, actual: 12300000, percent: 41 }] }
          }
        },
        {
          id: 'receiptsExport',
          name: 'Receipts Export',
          method: 'POST',
          path: '/biller/index.php',
          function: 'receiptsExport',
          summary: 'Paged export of receipts (receiptNo, billNo, amount, source, receiptedBy, date). Suitable for audit CSV. Max 10,000 rows/page.',
          auth: 'Bearer token with EXTERNAL AUDITOR or REPORTS right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'receiptsExport' },
            { field: 'from', type: 'string', required: true },
            { field: 'to', type: 'string', required: true },
            { field: 'page', type: 'number', required: false, notes: 'Default 1.' },
            { field: 'pageSize', type: 'number', required: false, notes: 'Default 5000, max 10000.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=receiptsExport" \\
  -F "from=2026-07-01" -F "to=2026-07-31" \\
  -F "page=1" -F "pageSize=5000"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { page: 1, pageSize: 5000, totalPages: 3, totalRows: 12450, rows: [
              { receiptNo: 'RC...', billNo: 'BLL...', amount: 5000, source: 'MPESA', receiptedBy: 'SYSTEM', date: '2026-07-15T10:38:00Z' }
            ] }
          }
        },
        {
          id: 'transactionsExport',
          name: 'Transactions Export',
          method: 'POST',
          path: '/biller/index.php',
          function: 'transactionsExport',
          summary: 'Paged export of payment transactions (transaction_code, billNo, amount, source, status, date). Suitable for reconciliation.',
          auth: 'Bearer token with EXTERNAL AUDITOR or REPORTS right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'transactionsExport' },
            { field: 'from', type: 'string', required: true },
            { field: 'to', type: 'string', required: true },
            { field: 'source', type: 'string', required: false, notes: 'MPESA / BANK / CASH / KCB / EQUITY / COOP.' },
            { field: 'page', type: 'number', required: false },
            { field: 'pageSize', type: 'number', required: false, notes: 'Max 10000.' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=transactionsExport" \\
  -F "from=2026-07-01" -F "to=2026-07-31" \\
  -F "source=MPESA"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { page: 1, pageSize: 5000, totalPages: 5, totalRows: 24680, rows: [
              { transaction_code: '<mpesa-ref>', billNo: 'BLL...', amount: 5000, source: 'MPESA', status: 'PAID', date: '2026-07-15T10:38:00Z' }
            ] }
          }
        },
        {
          id: 'exportIFMIS',
          name: 'IFMIS Export (SCOA-coded CSV)',
          method: 'POST',
          path: '/biller/index.php',
          function: 'exportIFMIS',
          summary: 'Streams a UTF-8 CSV of every PAID receipt line in the date range, tagged with the SCOA (Vote / Programme / Sub-Programme / Item / Sub-Item / Fund) from the fee master. File is ready to import into county IFMIS test or prod. Satisfies FR.47.',
          auth: 'Bearer token with FINANCE ADMIN or EXTERNAL AUDITOR right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'exportIFMIS' },
            { field: 'from', type: 'string', required: true, notes: 'YYYY-MM-DD (inclusive).' },
            { field: 'to', type: 'string', required: true, notes: 'YYYY-MM-DD (inclusive).' }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=exportIFMIS" -F "from=2026-07-01" -F "to=2026-07-31" \\
  -o ifmis-2026-07.csv`,
          responseSuccess: {
            status: 200, note: 'Response is a text/csv download with Content-Disposition attachment.',
            columns: ['GL Date','Reference','Vote','Programme','Sub-Programme','Item','Sub-Item','Fund','SCOA Code','Amount','Sub-County','Ward','Department','Stream','Fee ID','Fee Description','Bill No','Payment Mode','Payment Ref','Receipted By','Payer']
          },
          responseErrors: [
            { code: '"from + to (YYYY-MM-DD) required"', when: 'Missing or malformed date range.' }
          ]
        },
        {
          id: 'dashboardKPIs',
          name: 'Dashboard KPIs',
          method: 'POST',
          path: '/biller/index.php',
          function: 'dashboardKPIs',
          summary: 'Headline metrics for a period — total receipts count, total amount, top stream, top sub-county, average receipt value. Same numbers as the staff /dashboard.',
          auth: 'Bearer token with REPORTS right.',
          request: [
            { field: 'function', type: 'string', required: true, example: 'dashboardKPIs' },
            { field: 'from', type: 'string', required: true },
            { field: 'to', type: 'string', required: true }
          ],
          curl: `curl -X POST "https://api.craftcollect.africa/{county}/biller/index.php" \\
  -H "Authorization: Bearer <token>" \\
  -F "function=dashboardKPIs" \\
  -F "from=2026-07-01" -F "to=2026-07-31"`,
          responseSuccess: {
            status: 200, success: true, message: 'success',
            data: { totalReceipts: 24680, totalAmount: 107980000, avgReceipt: 4375, topStream: 'SBP', topSubCounty: 'MERU TOWN' }
          }
        }
      ]
    }
  ]
};
